import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  Chip,
  Alert,
  Grid,
  Divider,
  Avatar,
  Tabs,
  Tab,
  Tooltip,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add,
  Remove,
  Edit,
  Delete,
  People,
  ConfirmationNumber,
  CloudUpload,
  ContentCopy,
  QrCodeScanner,
  LocalOffer,
  Visibility,
  CreditCard,
  Person,
  Receipt,
  Lock,
  Send,
  Payments,
  Download,
  Share,
  WhatsApp,
  CheckCircle,
  Storefront,
} from '@mui/icons-material';
import Header from '../Header';
import AdminCheckIn from './AdminCheckIn';
import AdminCoupons from './AdminCoupons';
import EventPosterCropper from './EventPosterCropper';
import { isTicketCheckInOnly } from '../../../utils/adminAuth';
import { getTicketApiBase } from '../../../utils/ticketApiBase';

const API_BASE = () => getTicketApiBase();

const assetUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http')) return path;
  return `${API_BASE()}${path}`;
};

const formatPrice = (pence) => `£${((pence || 0) / 100).toFixed(2)}`;
const formatDate = (d) => d ? new Date(d).toLocaleString('en-GB') : '—';

const toCount = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? Math.round(n) : 0;
};

const PAYMENT_METHOD_CHIP = {
  cash: { label: 'Cash', bg: 'rgba(76,175,80,0.18)', color: '#81c784' },
  square: { label: 'Card', bg: 'rgba(102,126,234,0.18)', color: '#8c9eff' },
  complimentary: { label: 'Comp', bg: 'rgba(255,183,77,0.18)', color: '#ffb74d' },
};

// Falls back to the payment id prefix for tickets created before payment_method existed
const paymentMethodOf = (purchase) => {
  if (purchase?.payment_method) return purchase.payment_method;
  const paymentId = purchase?.payment_id || '';
  if (purchase?.source_type === 'complimentary' || paymentId.startsWith('FREE-')) return 'complimentary';
  if (paymentId.startsWith('CASH-')) return 'cash';
  return 'square';
};

/**
 * Prefer production HTTPS QR URLs when available.
 * On local HTTPS CRA, use same-origin `/api/...` (proxied to the API) so the
 * browser does not block http images as mixed content.
 */
const qrImageSrc = (ticketId, qrImageUrl) => {
  if (qrImageUrl && /^https:\/\//i.test(qrImageUrl) && !/localhost|127\.0\.0\.1/i.test(qrImageUrl)) {
    return qrImageUrl;
  }
  const path = `/api/tickets/qr/${encodeURIComponent(ticketId)}`;
  if (typeof window !== 'undefined' && window.location.protocol === 'https:' && /^http:\/\//i.test(process.env.REACT_APP_API_URL || '')) {
    return path;
  }
  return `${API_BASE() || ''}${path}`;
};

const DetailRow = ({ label, value, mono }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid #2a2a2a' }}>
    <Typography variant="body2" sx={{ color: '#999' }}>{label}</Typography>
    <Typography variant="body2" sx={{ color: '#fff', fontFamily: mono ? 'monospace' : 'inherit', textAlign: 'right', maxWidth: '60%', wordBreak: 'break-all' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

const SectionCard = ({ title, icon, children }) => (
  <Paper sx={{ p: 2, backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: 2, mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
      {icon}
      <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700 }}>{title}</Typography>
    </Box>
    {children}
  </Paper>
);

const emptyTicketType = () => ({
  id: null,
  name: '',
  price: '',
  maxTickets: '',
  markSoldOut: false,
  showFewLeft: false,
  showFewLeftDummy: false,
  fewLeftThreshold: '10',
  active: true,
});

const parsePosterCrop = (raw) => {
  if (!raw) return null;
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!parsed || !(Number(parsed.size) > 0)) return null;
    return {
      x: Number(parsed.x) || 0,
      y: Number(parsed.y) || 0,
      size: Number(parsed.size),
    };
  } catch {
    return null;
  }
};

const emptyForm = {
  title: '',
  eventName: '',
  description: '',
  imageUrl: '',
  imageOriginalUrl: '',
  imageCrop: '',
  venue: '',
  venueLocationLink: '',
  city: '',
  country: '',
  eventDate: '',
  maxTickets: '',
  markSoldOut: false,
  showFewLeft: false,
  showFewLeftDummy: false,
  fewLeftThreshold: '10',
  active: true,
  applyPlatformFee: false,
  platformFee: '',
  platformFeeType: 'flat',
  visibility: 'public',
  entryType: 'paid',
  ticketSource: 'internal',
  externalTicketUrl: '',
  ticketTypes: [emptyTicketType()],
};

const emptyCashForm = {
  customerMode: 'customer', // 'customer' | 'box_office'
  name: '',
  email: '',
  couponCode: '',
  notes: '',
  sendEmail: true,
  quantities: {},
};

const isPlaceholderEmail = (email) => (email || '').endsWith('@noreply.local');

// Accepts either the issue-cash response or a purchase row (snake_case keys)
const buildTicketShareText = (source) => {
  if (!source) return '';
  const eventLabel = source.eventTitle || source.event_title || source.eventName || source.event_name || 'Event';
  const rawDate = source.eventDate || source.event_date;
  const venue = source.eventVenue || source.venue;
  const amount = source.amount;
  const when = rawDate
    ? new Date(rawDate).toLocaleString('en-GB', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : null;

  const entries = (source.entries || []).map((entry) => ({
    id: entry.entryTicketId || entry.entry_ticket_id,
    code: entry.verificationCode || entry.verification_code,
    type: entry.typeName || entry.type_name || 'Ticket',
  }));

  const lines = [
    `🎫 Your tickets for ${eventLabel}`,
    when ? `📅 ${when}` : null,
    venue ? `📍 ${venue}` : null,
    amount != null ? `Paid: ${formatPrice(amount)}` : null,
    '',
    ...entries.map((entry, index) => (
      `${index + 1}. ${entry.type}\n   ID: ${entry.id}\n   Code: ${entry.code}`
    )),
    '',
    'Your ticket PDF is attached. Show the QR code at the entrance.',
    'Mind Your Music',
  ];
  return lines.filter((line) => line != null).join('\n');
};

const triggerFileDownload = (file) => {
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = file.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const formatPriceRange = (event) => {
  if (event.visibility === 'private' || event.is_private) return '—';
  if (event.entry_type === 'free' || event.is_free_entry) return 'Free';
  if (event.ticket_source === 'external' || event.is_external_tickets) return 'External';
  const types = event.ticket_types || [];
  if (!types.length) return formatPrice(event.ticket_price_pence);
  const prices = types.map((t) => t.price_pence).filter(Boolean);
  if (!prices.length) return formatPrice(event.ticket_price_pence);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  return min === max ? formatPrice(min) : `${formatPrice(min)} – ${formatPrice(max)}`;
};

const accessChip = (event) => {
  if (event.visibility === 'private' || event.is_private) {
    return { label: 'Private', bg: 'rgba(144,202,249,0.15)', color: '#90caf9' };
  }
  if (event.entry_type === 'free' || event.is_free_entry) {
    return { label: 'Free entry', bg: 'rgba(129,199,132,0.15)', color: '#81c784' };
  }
  if (event.ticket_source === 'external' || event.is_external_tickets) {
    return { label: 'External tickets', bg: 'rgba(255,183,77,0.15)', color: '#ffb74d' };
  }
  return { label: 'Paid (site)', bg: 'rgba(102,126,234,0.15)', color: '#667eea' };
};

const TAB_MAP = { events: 0, checkin: 1, coupons: 2 };
const TAB_KEYS = ['events', 'checkin', 'coupons'];

const AdminTicketDashboard = () => {
  const checkInOnly = isTicketCheckInOnly();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab');
  const mainTab = checkInOnly ? TAB_MAP.checkin : (TAB_MAP[tabParam] ?? 0);

  const handleTabChange = (_, value) => {
    if (checkInOnly) return;
    setSearchParams({ tab: TAB_KEYS[value] }, { replace: true });
  };

  useEffect(() => {
    if (checkInOnly && tabParam !== 'checkin') {
      setSearchParams({ tab: 'checkin' }, { replace: true });
    }
  }, [checkInOnly, tabParam, setSearchParams]);

  const [events, setEvents] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [purchasesOpen, setPurchasesOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [editingEvent, setEditingEvent] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [purchases, setPurchases] = useState([]);
  const [purchaseFilters, setPurchaseFilters] = useState({
    coupon: 'all',
    purchaseDate: '',
    checkIn: 'all',
    method: 'all',
  });
  const [cashOpen, setCashOpen] = useState(false);
  const [cashForm, setCashForm] = useState(emptyCashForm);
  const [cashQuote, setCashQuote] = useState(null);
  const [cashQuoteError, setCashQuoteError] = useState('');
  const [cashSubmitting, setCashSubmitting] = useState(false);
  const [cashError, setCashError] = useState('');
  const [cashResult, setCashResult] = useState(null);
  const [cashDownloading, setCashDownloading] = useState(false);
  const [cashActionMessage, setCashActionMessage] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [shareBusy, setShareBusy] = useState(false);
  const [emailPrompt, setEmailPrompt] = useState(null);
  const [emailPromptValue, setEmailPromptValue] = useState('');
  const [emailPromptError, setEmailPromptError] = useState('');
  const [emailPromptSending, setEmailPromptSending] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [savedCrop, setSavedCrop] = useState(null);
  const [posterCrop, setPosterCrop] = useState(null);
  const [togglingEventId, setTogglingEventId] = useState(null);
  const [resendingTicketId, setResendingTicketId] = useState(null);
  const [downloadingTicketId, setDownloadingTicketId] = useState(null);
  const [detailMessage, setDetailMessage] = useState('');
  const [detailError, setDetailError] = useState('');

  useEffect(() => {
    if (!checkInOnly) {
      fetchEvents();
    }
  }, [checkInOnly]);

  const fetchEvents = async () => {
    try {
      const res = await fetch(`${API_BASE()}/api/tickets/admin/events`);
      const data = await res.json();
      if (data.success) setEvents(data.events);
    } catch (e) {
      console.error('Failed to fetch events', e);
    }
  };

  const fetchPurchases = async (eventId, { keepOpen = false } = {}) => {
    try {
      const res = await fetch(`${API_BASE()}/api/tickets/admin/events/${eventId}/purchases`);
      const data = await res.json();
      if (data.success) {
        setSelectedEvent(data.event);
        setPurchases(data.purchases);
        if (!keepOpen) {
          setPurchaseFilters({ coupon: 'all', purchaseDate: '', checkIn: 'all', method: 'all' });
          setPurchasesOpen(true);
        }
      }
    } catch (e) {
      console.error('Failed to fetch purchases', e);
    }
  };

  const purchaseCouponOptions = useMemo(() => {
    const codes = [...new Set(
      purchases
        .map((p) => (p.coupon_code || '').trim().toUpperCase())
        .filter(Boolean)
    )].sort();
    return codes;
  }, [purchases]);

  const filteredPurchases = useMemo(() => {
    return purchases.filter((p) => {
      const code = (p.coupon_code || '').trim().toUpperCase();
      if (purchaseFilters.coupon === 'none') {
        if (code) return false;
      } else if (purchaseFilters.coupon !== 'all') {
        if (code !== purchaseFilters.coupon) return false;
      }

      if (purchaseFilters.purchaseDate) {
        if (!p.created_at) return false;
        const day = new Date(p.created_at).toLocaleDateString('en-CA');
        if (day !== purchaseFilters.purchaseDate) return false;
      }

      if (purchaseFilters.checkIn === 'checked_in' && !p.checked_in) return false;
      if (purchaseFilters.checkIn === 'pending' && p.checked_in) return false;

      if (purchaseFilters.method !== 'all' && paymentMethodOf(p) !== purchaseFilters.method) return false;

      return true;
    });
  }, [purchases, purchaseFilters]);

  const purchaseStats = useMemo(() => {
    const source = filteredPurchases;
    return {
      revenue: source.reduce((sum, p) => sum + (p.amount || 0), 0),
      cashRevenue: source.reduce((sum, p) => (
        paymentMethodOf(p) === 'cash' ? sum + (p.amount || 0) : sum
      ), 0),
      transactions: source.length,
      ticketsSold: source.reduce((sum, p) => sum + toCount(p.quantity), 0),
    };
  }, [filteredPurchases]);

  const cashItems = useMemo(() => (
    Object.entries(cashForm.quantities)
      .map(([ticketTypeId, quantity]) => ({ ticketTypeId: Number(ticketTypeId), quantity: toCount(quantity) }))
      .filter((item) => item.quantity > 0)
  ), [cashForm.quantities]);

  const cashTicketTypes = useMemo(() => (
    (selectedEvent?.ticket_types || []).filter((type) => type.active !== 0 && type.active !== false)
  ), [selectedEvent]);

  // Price the order server-side so the desk always collects the exact total
  useEffect(() => {
    if (!cashOpen || cashResult || !selectedEvent || cashItems.length === 0) {
      if (!cashResult) {
        setCashQuote(null);
        setCashQuoteError('');
      }
      return undefined;
    }

    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE()}/api/tickets/admin/tickets/quote`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            eventId: selectedEvent.id,
            items: cashItems,
            couponCode: appliedCoupon || undefined,
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (data.success) {
          setCashQuote(data);
          setCashQuoteError('');
        } else {
          setCashQuote(null);
          setCashQuoteError(data.message || 'Could not price this order');
        }
      } catch (e) {
        if (!cancelled) {
          setCashQuote(null);
          setCashQuoteError('Could not price this order');
        }
      }
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [cashOpen, cashResult, selectedEvent, cashItems, appliedCoupon]);

  const openCashDialog = () => {
    setCashForm(emptyCashForm);
    setCashQuote(null);
    setCashQuoteError('');
    setCashError('');
    setCashResult(null);
    setCashActionMessage('');
    setAppliedCoupon(null);
    setCashOpen(true);
  };

  const resetCashDialogForNextSale = () => {
    setCashForm((prev) => ({
      ...emptyCashForm,
      customerMode: prev.customerMode,
      sendEmail: prev.customerMode === 'customer',
    }));
    setCashQuote(null);
    setCashQuoteError('');
    setCashError('');
    setCashResult(null);
    setCashActionMessage('');
    setAppliedCoupon(null);
  };

  const handleApplyCoupon = async () => {
    const code = cashForm.couponCode.trim().toUpperCase();
    if (!code || applyingCoupon || !selectedEvent) return;
    if (cashItems.length === 0) {
      setCashError('Add tickets before applying a discount code');
      return;
    }

    setApplyingCoupon(true);
    setCashError('');
    try {
      const res = await fetch(`${API_BASE()}/api/tickets/admin/tickets/quote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ eventId: selectedEvent.id, items: cashItems, couponCode: code }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'This discount code cannot be used');
      }
      setAppliedCoupon(code);
      setCashQuote(data);
      setCashQuoteError('');
      setCashActionMessage(`Discount code ${code} applied`);
    } catch (e) {
      setAppliedCoupon(null);
      setCashError(e.message || 'This discount code cannot be used');
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCashForm((prev) => ({ ...prev, couponCode: '' }));
    setCashActionMessage('');
    setCashError('');
  };

  const setCashQuantity = (typeId, nextValue) => {
    setCashForm((prev) => ({
      ...prev,
      quantities: { ...prev.quantities, [typeId]: Math.max(0, nextValue) },
    }));
  };

  const setCashCustomerMode = (_, mode) => {
    if (!mode) return;
    setCashForm((prev) => ({
      ...prev,
      customerMode: mode,
      sendEmail: mode === 'customer' ? prev.sendEmail : false,
      name: mode === 'box_office' ? '' : prev.name,
      email: mode === 'box_office' ? '' : prev.email,
    }));
    setCashError('');
  };

  const handleIssueCashTicket = async () => {
    if (cashSubmitting || !selectedEvent) return;

    const isBoxOffice = cashForm.customerMode === 'box_office';
    if (!isBoxOffice && cashForm.sendEmail && !cashForm.email.trim()) {
      setCashError('Enter an email to send tickets, or turn off email delivery');
      return;
    }
    if (cashItems.length === 0) {
      setCashError('Select at least one ticket');
      return;
    }

    setCashSubmitting(true);
    setCashError('');
    setCashActionMessage('');

    try {
      const res = await fetch(`${API_BASE()}/api/tickets/admin/tickets/issue-cash`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          eventId: selectedEvent.id,
          customerMode: cashForm.customerMode,
          name: isBoxOffice ? undefined : (cashForm.name.trim() || undefined),
          email: isBoxOffice ? undefined : (cashForm.email.trim() || undefined),
          items: cashItems,
          couponCode: appliedCoupon || undefined,
          notes: cashForm.notes.trim() || undefined,
          sendEmail: isBoxOffice ? false : cashForm.sendEmail,
        }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to issue tickets');
      }

      setCashResult(data);
      setCashQuote(null);
      await fetchPurchases(selectedEvent.id, { keepOpen: true });
    } catch (e) {
      console.error('Issue cash ticket error', e);
      setCashError(e.message || 'Failed to issue tickets');
    } finally {
      setCashSubmitting(false);
    }
  };

  // Share/download feedback goes to whichever dialog the admin is working in
  const setShareFeedback = (context, message, isError = false) => {
    if (context === 'cash') {
      if (isError) setCashError(message);
      else setCashActionMessage(message);
    } else {
      if (isError) setDetailError(message);
      else setDetailMessage(message);
    }
  };

  const fetchTicketPdfFile = async (ticketId) => {
    const res = await fetch(
      `${API_BASE()}/api/tickets/admin/tickets/${encodeURIComponent(ticketId)}/pdf`,
      { credentials: 'include' }
    );
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || 'Failed to generate ticket PDF');
    }
    const blob = await res.blob();
    return new File([blob], `MYM-Tickets-${ticketId}.pdf`, { type: 'application/pdf' });
  };

  const handleDownloadPdf = async (ticketId, context) => {
    if (!ticketId || cashDownloading || downloadingTicketId) return;
    if (context === 'cash') setCashDownloading(true);
    else setDownloadingTicketId(ticketId);
    setShareFeedback(context, '');
    try {
      triggerFileDownload(await fetchTicketPdfFile(ticketId));
      setShareFeedback(context, 'Ticket PDF downloaded');
    } catch (e) {
      console.error('Ticket PDF download error', e);
      setShareFeedback(context, e.message || 'Failed to download PDF', true);
    } finally {
      if (context === 'cash') setCashDownloading(false);
      else setDownloadingTicketId(null);
    }
  };

  /**
   * Shares the ticket PDF itself. Mobile browsers hand the file to the OS share
   * sheet (WhatsApp included); desktop browsers cannot attach files to a share,
   * so the PDF is downloaded and WhatsApp Web is opened for manual attach.
   */
  const sharePdf = async (ticketId, source, { context, whatsapp = false }) => {
    if (!ticketId || shareBusy) return;
    setShareBusy(true);
    setShareFeedback(context, '');

    const text = buildTicketShareText(source);
    const title = `Tickets — ${source?.eventTitle || source?.event_title || 'Mind Your Music'}`;

    try {
      const file = await fetchTicketPdfFile(ticketId);

      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title, text });
        setShareFeedback(context, 'Ticket PDF shared');
        return;
      }

      triggerFileDownload(file);
      if (whatsapp) {
        window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
        setShareFeedback(context, 'PDF downloaded — attach it in the WhatsApp window that just opened');
      } else {
        setShareFeedback(context, 'This browser cannot attach files to a share — PDF downloaded instead');
      }
    } catch (e) {
      if (e?.name === 'AbortError') return;
      console.error('Share ticket PDF error', e);
      setShareFeedback(context, e.message || 'Could not share the ticket PDF', true);
    } finally {
      setShareBusy(false);
    }
  };

  const openEmailPrompt = (ticketId, defaultEmail, context) => {
    setEmailPrompt({ ticketId, context });
    setEmailPromptValue(isPlaceholderEmail(defaultEmail) ? '' : (defaultEmail || ''));
    setEmailPromptError('');
  };

  const handleSendPdfToEmail = async () => {
    const email = emailPromptValue.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setEmailPromptError('Enter a valid email address');
      return;
    }

    setEmailPromptSending(true);
    setEmailPromptError('');
    try {
      const res = await fetch(
        `${API_BASE()}/api/tickets/admin/tickets/${encodeURIComponent(emailPrompt.ticketId)}/send-pdf`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ email }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to send ticket email');
      }

      setShareFeedback(emailPrompt.context, `Ticket PDF sent to ${email}`);
      setEmailPrompt(null);
      if (selectedEvent) await fetchPurchases(selectedEvent.id, { keepOpen: true });
    } catch (e) {
      console.error('Send ticket PDF email error', e);
      setEmailPromptError(e.message || 'Failed to send ticket email');
    } finally {
      setEmailPromptSending(false);
    }
  };

  const handleCopyCashDetails = async () => {
    try {
      await navigator.clipboard.writeText(buildTicketShareText(cashResult));
      setCashActionMessage('Ticket details copied');
    } catch (e) {
      setCashError('Could not copy to clipboard');
    }
  };

  const handleOpenCreate = () => {
    setEditingEvent(null);
    setFormData(emptyForm);
    setImagePreview(null);
    setSavedCrop(null);
    setPosterCrop(null);
    setError('');
    setDialogOpen(true);
  };

  const handleOpenEdit = (event) => {
    setEditingEvent(event);
    const types = (event.ticket_types || []).length
      ? event.ticket_types.map((type) => ({
          id: type.id,
          name: type.name,
          price: (type.price_pence / 100).toFixed(2),
          maxTickets: type.max_tickets ? String(type.max_tickets) : '',
          markSoldOut: !!type.mark_sold_out,
          showFewLeft: !!type.show_few_left,
          showFewLeftDummy: !!type.show_few_left_dummy,
          fewLeftThreshold: type.few_left_threshold ? String(type.few_left_threshold) : '10',
          active: !!type.active,
        }))
      : [{
          ...emptyTicketType(),
          name: 'General Admission',
          price: (event.ticket_price_pence / 100).toFixed(2),
          maxTickets: event.max_tickets ? String(event.max_tickets) : '',
          markSoldOut: !!event.mark_sold_out,
          showFewLeft: !!event.show_few_left,
          showFewLeftDummy: !!event.show_few_left_dummy,
          fewLeftThreshold: event.few_left_threshold ? String(event.few_left_threshold) : '10',
        }];

    setFormData({
      title: event.title,
      eventName: event.event_name,
      description: event.description || '',
      imageUrl: event.image_url || '',
      imageOriginalUrl: event.image_original_url || event.image_url || '',
      imageCrop: event.image_crop || '',
      venue: event.venue || '',
      venueLocationLink: event.venue_location_link || '',
      city: event.city || '',
      country: event.country || '',
      eventDate: event.event_date ? event.event_date.slice(0, 16) : '',
      maxTickets: event.max_tickets ? String(event.max_tickets) : '',
      markSoldOut: !!event.mark_sold_out,
      showFewLeft: !!event.show_few_left,
      showFewLeftDummy: !!event.show_few_left_dummy,
      fewLeftThreshold: event.few_left_threshold ? String(event.few_left_threshold) : '10',
      active: !!event.active,
      applyPlatformFee: !!event.platform_fee_enabled,
      platformFee: event.platform_fee_enabled && event.platform_fee_pence
        ? (event.platform_fee_pence / 100).toFixed(2)
        : '',
      platformFeeType: event.platform_fee_type === 'per_ticket' ? 'per_ticket' : 'flat',
      visibility: event.visibility === 'private' ? 'private' : 'public',
      entryType: event.entry_type === 'free' ? 'free' : 'paid',
      ticketSource: event.ticket_source === 'external'
        ? 'external'
        : event.ticket_source === 'none'
          ? 'none'
          : 'internal',
      externalTicketUrl: event.external_ticket_url || '',
      ticketTypes: types,
    });
    setImagePreview(assetUrl(event.image_original_url || event.image_url));
    setSavedCrop(parsePosterCrop(event.image_crop));
    setPosterCrop(parsePosterCrop(event.image_crop));
    setError('');
    setDialogOpen(true);
  };

  const updateTicketType = (index, field, value) => {
    setFormData((prev) => {
      const ticketTypes = [...prev.ticketTypes];
      ticketTypes[index] = { ...ticketTypes[index], [field]: value };
      return { ...prev, ticketTypes };
    });
  };

  const addTicketType = () => {
    setFormData((prev) => ({
      ...prev,
      ticketTypes: [...prev.ticketTypes, emptyTicketType()],
    }));
  };

  const removeTicketType = (index) => {
    setFormData((prev) => {
      if (prev.ticketTypes.length <= 1) return prev;
      return {
        ...prev,
        ticketTypes: prev.ticketTypes.filter((_, i) => i !== index),
      };
    });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const form = new FormData();
    form.append('image', file);

    try {
      const res = await fetch(`${API_BASE()}/api/tickets/admin/events/upload-image`, {
        method: 'POST',
        body: form,
      });
      const data = await res.json();
      if (data.success) {
        setFormData((prev) => ({
          ...prev,
          imageUrl: data.imageUrl,
          imageOriginalUrl: data.imageUrl,
          imageCrop: '',
        }));
        setImagePreview(assetUrl(data.imageUrl));
        setSavedCrop(null);
        setPosterCrop(null);
      } else {
        setError(data.message || 'Upload failed');
      }
    } catch (err) {
      setError('Image upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    setError('');
    if (!formData.title || !formData.eventName) {
      setError('Title and event name are required');
      return;
    }

    const isPrivate = formData.visibility === 'private';
    const isFree = !isPrivate && formData.entryType === 'free';
    const isExternal = !isPrivate && !isFree && formData.ticketSource === 'external';
    const isInternal = !isPrivate && !isFree && !isExternal;

    if (isPrivate && (!formData.city.trim() || !formData.country.trim())) {
      setError('City and country are required for private events');
      return;
    }

    if (isExternal) {
      const url = formData.externalTicketUrl.trim();
      if (!url) {
        setError('External ticket URL is required');
        return;
      }
      try {
        const parsed = new URL(url);
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
          setError('External ticket URL must start with http:// or https://');
          return;
        }
      } catch {
        setError('External ticket URL must be a valid http or https link');
        return;
      }
    }

    const ticketTypes = [];
    if (isInternal) {
      for (let i = 0; i < formData.ticketTypes.length; i++) {
        const type = formData.ticketTypes[i];
        if (!type.name.trim()) {
          setError(`Ticket type ${i + 1} needs a name`);
          return;
        }
        const pricePence = Math.round(parseFloat(type.price) * 100);
        if (isNaN(pricePence) || pricePence < 100) {
          setError(`"${type.name}" price must be at least £1.00`);
          return;
        }
        const hasTypeMax = !!type.maxTickets;
        ticketTypes.push({
          id: type.id || undefined,
          name: type.name.trim(),
          pricePence,
          maxTickets: hasTypeMax ? parseInt(type.maxTickets, 10) : null,
          markSoldOut: type.markSoldOut,
          showFewLeftDummy: type.showFewLeftDummy,
          showFewLeft: type.showFewLeftDummy ? false : (hasTypeMax ? type.showFewLeft : false),
          fewLeftThreshold: hasTypeMax && type.showFewLeft && !type.showFewLeftDummy
            ? parseInt(type.fewLeftThreshold, 10) || 10
            : 10,
          active: type.active,
        });
      }
    }

    const hasMaxTickets = isInternal && !!formData.maxTickets;
    let platformFeePence = 0;
    if (isInternal && formData.applyPlatformFee) {
      platformFeePence = Math.round(parseFloat(formData.platformFee) * 100);
      if (isNaN(platformFeePence) || platformFeePence < 1) {
        setError('Platform fee amount is required when enabled');
        return;
      }
    }

    let imageUrl = formData.imageUrl;
    let imageOriginalUrl = formData.imageOriginalUrl || formData.imageUrl || '';
    let imageCrop = formData.imageCrop || '';

    if (imageOriginalUrl) {
      if (!posterCrop) {
        setError('Wait for the poster preview to finish loading, then adjust the crop');
        return;
      }
      try {
        const cropRes = await fetch(`${API_BASE()}/api/tickets/admin/events/crop-image`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sourceUrl: imageOriginalUrl,
            x: posterCrop.x,
            y: posterCrop.y,
            size: posterCrop.size,
          }),
        });
        const cropData = await cropRes.json();
        if (!cropData.success) {
          setError(cropData.message || 'Could not apply the poster crop');
          return;
        }
        imageUrl = cropData.imageUrl;
        imageCrop = JSON.stringify(cropData.crop);
      } catch {
        setError('Could not apply the poster crop');
        return;
      }
    }

    const payload = {
      title: formData.title,
      eventName: formData.eventName,
      description: formData.description,
      imageUrl,
      imageOriginalUrl: imageOriginalUrl || null,
      imageCrop: imageCrop || null,
      venue: formData.venue,
      venueLocationLink: isPrivate ? null : (formData.venueLocationLink || null),
      city: formData.city.trim() || null,
      country: formData.country.trim() || null,
      eventDate: formData.eventDate || null,
      maxTickets: hasMaxTickets ? parseInt(formData.maxTickets, 10) : null,
      markSoldOut: isInternal && formData.markSoldOut,
      showFewLeftDummy: isInternal && formData.showFewLeftDummy,
      showFewLeft: isInternal && formData.showFewLeftDummy ? false : (hasMaxTickets ? formData.showFewLeft : false),
      fewLeftThreshold: hasMaxTickets && formData.showFewLeft && !formData.showFewLeftDummy
        ? parseInt(formData.fewLeftThreshold, 10) || 10
        : 10,
      active: formData.active,
      applyPlatformFee: isInternal && formData.applyPlatformFee,
      platformFeePence: isInternal ? platformFeePence : 0,
      platformFeeType: isInternal && formData.applyPlatformFee ? formData.platformFeeType : 'flat',
      visibility: isPrivate ? 'private' : 'public',
      entryType: isPrivate || isFree ? 'free' : 'paid',
      ticketSource: isInternal ? 'internal' : (isExternal ? 'external' : 'none'),
      externalTicketUrl: isExternal ? formData.externalTicketUrl.trim() : null,
      ticketTypes,
    };

    try {
      const url = editingEvent
        ? `${API_BASE()}/api/tickets/admin/events/${editingEvent.id}`
        : `${API_BASE()}/api/tickets/admin/events`;
      const res = await fetch(url, {
        method: editingEvent ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!data.success) {
        setError(data.message || 'Failed to save event');
        return;
      }
      setDialogOpen(false);
      fetchEvents();
    } catch (e) {
      setError('Request failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    try {
      const res = await fetch(`${API_BASE()}/api/tickets/admin/events/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (!data.success) {
        alert(data.message || 'Delete failed');
        return;
      }
      fetchEvents();
    } catch (e) {
      console.error('Delete error', e);
    }
  };

  const copyTicketLink = (eventId) => {
    const link = `${window.location.origin}/tickets/${eventId}`;
    navigator.clipboard.writeText(link);
  };

  const handleToggleActive = async (event) => {
    if (togglingEventId) return;
    const nextActive = !event.active;
    setTogglingEventId(event.id);
    setEvents((prev) => prev.map((item) => (
      item.id === event.id ? { ...item, active: nextActive ? 1 : 0 } : item
    )));

    try {
      const res = await fetch(`${API_BASE()}/api/tickets/admin/events/${event.id}/toggle-active`, {
        method: 'PATCH',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to update event status');
      }
      setEvents((prev) => prev.map((item) => (
        item.id === event.id
          ? { ...item, active: data.active ? 1 : 0 }
          : item
      )));
    } catch (e) {
      console.error('Toggle active error', e);
      setEvents((prev) => prev.map((item) => (
        item.id === event.id ? { ...item, active: event.active } : item
      )));
      alert(e.message || 'Failed to update event status');
    } finally {
      setTogglingEventId(null);
    }
  };

  const openPurchaseDetail = (purchase) => {
    setSelectedPurchase(purchase);
    setDetailMessage('');
    setDetailError('');
    setDetailOpen(true);
  };

  const handleResendTicketEmail = async () => {
    if (!selectedPurchase?.ticket_id || resendingTicketId) return;
    setResendingTicketId(selectedPurchase.ticket_id);
    setDetailMessage('');
    setDetailError('');
    try {
      const res = await fetch(
        `${API_BASE()}/api/tickets/admin/tickets/${encodeURIComponent(selectedPurchase.ticket_id)}/resend`,
        { method: 'POST', credentials: 'include' }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.message || 'Failed to resend ticket email');
      }
      setDetailMessage(data.message || `Ticket PDF resent to ${selectedPurchase.email}`);
    } catch (e) {
      console.error('Resend ticket email error', e);
      setDetailError(e.message || 'Failed to resend ticket email');
    } finally {
      setResendingTicketId(null);
    }
  };

  const formatCard = (purchase) => {
    if (!purchase.card_brand && !purchase.card_last4) return '—';
    const brand = purchase.card_brand || 'Card';
    const last4 = purchase.card_last4 ? ` •••• ${purchase.card_last4}` : '';
    return `${brand}${last4}`;
  };

  const isPrivateEvent = formData.visibility === 'private';
  const isPaidPublic = !isPrivateEvent && formData.entryType === 'paid';
  const needsInternalTickets = isPaidPublic && formData.ticketSource === 'internal';
  const needsExternalUrl = isPaidPublic && formData.ticketSource === 'external';

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#121210' }}>
      <Header title="Ticket Dashboard" />
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        {!checkInOnly && (
          <Tabs
            value={mainTab}
            onChange={handleTabChange}
            sx={{
              mb: 3,
              borderBottom: '1px solid #333',
              '& .MuiTab-root': { color: '#999', textTransform: 'none', fontWeight: 600, minHeight: 48 },
              '& .Mui-selected': { color: '#667eea' },
            }}
            TabIndicatorProps={{ sx: { backgroundColor: '#667eea' } }}
          >
            <Tab icon={<ConfirmationNumber />} iconPosition="start" label="Events" />
            <Tab icon={<QrCodeScanner />} iconPosition="start" label="Check-In" />
            <Tab icon={<LocalOffer />} iconPosition="start" label="Coupons" />
          </Tabs>
        )}

        {!checkInOnly && mainTab === 0 && (
          <>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography variant="h6" sx={{ color: '#fff' }}>Ticket Events</Typography>
            <Typography variant="body2" sx={{ color: '#999' }}>
              Create events, set prices, and view ticket purchases
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleOpenCreate}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}
          >
            Create Event
          </Button>
        </Box>

        <TableContainer component={Paper} sx={{ backgroundColor: '#1a1a1a', mb: 3 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: '#999' }}>Event</TableCell>
                <TableCell sx={{ color: '#999' }}>Price</TableCell>
                <TableCell sx={{ color: '#999' }}>Sold</TableCell>
                <TableCell sx={{ color: '#999' }}>Revenue</TableCell>
                <TableCell sx={{ color: '#999' }}>Checked In</TableCell>
                <TableCell sx={{ color: '#999' }}>Status</TableCell>
                <TableCell sx={{ color: '#999' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {events.map((event) => (
                <TableRow key={event.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Avatar
                        src={assetUrl(event.image_url)}
                        variant="rounded"
                        sx={{ width: 48, height: 48, bgcolor: '#333' }}
                      >
                        <ConfirmationNumber />
                      </Avatar>
                      <Box>
                        <Typography sx={{ color: '#fff', fontWeight: 600 }}>{event.title}</Typography>
                        <Typography variant="caption" sx={{ color: '#667eea', display: 'block' }}>{event.event_name}</Typography>
                        <Chip
                          size="small"
                          icon={event.visibility === 'private' || event.is_private ? <Lock sx={{ fontSize: 12, color: `${accessChip(event).color} !important` }} /> : undefined}
                          label={accessChip(event).label}
                          sx={{
                            mt: 0.5,
                            height: 20,
                            fontSize: 10,
                            backgroundColor: accessChip(event).bg,
                            color: accessChip(event).color,
                          }}
                        />
                        {event.event_date && (
                          <Typography variant="caption" sx={{ color: '#666', display: 'block' }}>
                            {formatDate(event.event_date)}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell sx={{ color: '#4caf50', fontWeight: 600 }}>
                    {formatPriceRange(event)}
                  </TableCell>
                  <TableCell sx={{ color: '#fff' }}>
                    {toCount(event.tickets_sold)}
                    {!!event.mark_sold_out && (
                      <Chip label="Sold Out" size="small" sx={{ ml: 1, backgroundColor: 'rgba(244,67,54,0.2)', color: '#f44336', height: 20, fontSize: 10 }} />
                    )}
                    {!!event.is_few_left && !event.mark_sold_out && (
                      <Chip label="Few Left" size="small" sx={{ ml: 1, backgroundColor: 'rgba(255,152,0,0.2)', color: '#ff9800', height: 20, fontSize: 10 }} />
                    )}
                  </TableCell>
                  <TableCell sx={{ color: '#fff' }}>{formatPrice(event.total_revenue)}</TableCell>
                  <TableCell sx={{ color: '#fff' }}>{toCount(event.checked_in_count)}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Switch
                        size="small"
                        checked={!!event.active}
                        disabled={togglingEventId === event.id}
                        onChange={() => handleToggleActive(event)}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' },
                          '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' },
                        }}
                      />
                      <Chip
                        label={event.active ? 'Active' : 'Inactive'}
                        size="small"
                        onClick={() => handleToggleActive(event)}
                        disabled={togglingEventId === event.id}
                        sx={{
                          cursor: togglingEventId === event.id ? 'default' : 'pointer',
                          backgroundColor: event.active ? 'rgba(76,175,80,0.2)' : 'rgba(244,67,54,0.2)',
                          color: event.active ? '#4caf50' : '#f44336',
                        }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => fetchPurchases(event.id)} sx={{ color: '#4caf50' }} title="View Purchases">
                      <People fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => copyTicketLink(event.id)} sx={{ color: '#999' }} title="Copy Ticket Link">
                      <ContentCopy fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleOpenEdit(event)} sx={{ color: '#667eea' }}>
                      <Edit fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(event.id)} sx={{ color: '#f44336' }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {events.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} sx={{ textAlign: 'center', color: '#666', py: 4 }}>
                    No events created yet. Click "Create Event" to get started.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
          </>
        )}

        {(checkInOnly || mainTab === 1) && <AdminCheckIn embedded />}
        {!checkInOnly && mainTab === 2 && <AdminCoupons embedded />}
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { backgroundColor: '#1a1a1a', color: '#fff' } }}
      >
        <DialogTitle>{editingEvent ? 'Edit Event' : 'Create Event'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          <Box sx={{ mt: 1, mb: 2, textAlign: 'center' }}>
            {imagePreview ? (
              <EventPosterCropper
                key={imagePreview}
                src={imagePreview}
                initialCrop={savedCrop}
                onChange={setPosterCrop}
              />
            ) : (
              <Box sx={{ width: 280, height: 280, mx: 'auto', bgcolor: '#2a2a2a', borderRadius: 2, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <Typography sx={{ color: '#666', px: 2 }}>No image — upload a poster, then crop it to the square</Typography>
              </Box>
            )}
            <Button
              component="label"
              variant="outlined"
              startIcon={<CloudUpload />}
              disabled={uploading}
              sx={{ color: '#667eea', borderColor: '#667eea' }}
            >
              {uploading ? 'Uploading...' : 'Upload Image'}
              <input type="file" hidden accept="image/jpeg,image/png,.jpg,.jpeg,.png" onChange={handleImageUpload} />
            </Button>
          </Box>

          <TextField fullWidth label="Title" value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} sx={{ mb: 2 }} InputLabelProps={{ sx: { color: '#999' } }} InputProps={{ sx: { color: '#fff' } }} helperText="Short heading shown on ticket page" />
          <TextField fullWidth label="Event Name" value={formData.eventName} onChange={(e) => setFormData({ ...formData, eventName: e.target.value })} sx={{ mb: 2 }} InputLabelProps={{ sx: { color: '#999' } }} InputProps={{ sx: { color: '#fff' } }} />
          <TextField fullWidth label="Description" value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={3} sx={{ mb: 2 }} InputLabelProps={{ sx: { color: '#999' } }} InputProps={{ sx: { color: '#fff' } }} />

          <Divider sx={{ borderColor: '#333', my: 2 }} />
          <Typography variant="subtitle2" sx={{ color: '#667eea', mb: 1.5, fontWeight: 700 }}>
            Event Access
          </Typography>
          <FormControl sx={{ display: 'block', mb: 2 }}>
            <FormLabel sx={{ color: '#999', fontSize: 13, mb: 0.5 }}>Event Visibility</FormLabel>
            <RadioGroup
              row
              value={formData.visibility}
              onChange={(e) => {
                const visibility = e.target.value;
                setFormData((prev) => ({
                  ...prev,
                  visibility,
                  entryType: visibility === 'private'
                    ? 'free'
                    : (prev.visibility === 'private' ? 'paid' : (prev.entryType === 'free' ? 'free' : 'paid')),
                  ticketSource: visibility === 'private'
                    ? 'none'
                    : (prev.visibility === 'private'
                      ? 'internal'
                      : (prev.entryType === 'free' ? 'none' : (prev.ticketSource === 'external' ? 'external' : 'internal'))),
                  externalTicketUrl: visibility === 'private' ? '' : prev.externalTicketUrl,
                }));
              }}
            >
              <FormControlLabel
                value="public"
                control={<Radio sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />}
                label="Public"
                sx={{ color: '#ccc' }}
              />
              <FormControlLabel
                value="private"
                control={<Radio sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />}
                label="Private"
                sx={{ color: '#ccc' }}
              />
            </RadioGroup>
          </FormControl>

          {isPrivateEvent && (
            <Alert severity="info" sx={{ mb: 2, backgroundColor: 'rgba(144,202,249,0.08)', color: '#90caf9' }}>
              Private events only show city and country publicly. Venue, tickets, and registration are hidden.
            </Alert>
          )}

          {!isPrivateEvent && (
            <FormControl sx={{ display: 'block', mb: 2 }}>
              <FormLabel sx={{ color: '#999', fontSize: 13, mb: 0.5 }}>Entry / Ticket Type</FormLabel>
              <RadioGroup
                row
                value={formData.entryType}
                onChange={(e) => {
                  const entryType = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    entryType,
                    ticketSource: entryType === 'free' ? 'none' : (prev.ticketSource === 'external' ? 'external' : 'internal'),
                    externalTicketUrl: entryType === 'free' ? '' : prev.externalTicketUrl,
                    applyPlatformFee: entryType === 'free' ? false : prev.applyPlatformFee,
                  }));
                }}
              >
                <FormControlLabel
                  value="paid"
                  control={<Radio sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />}
                  label="Paid"
                  sx={{ color: '#ccc' }}
                />
                <FormControlLabel
                  value="free"
                  control={<Radio sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />}
                  label="Free"
                  sx={{ color: '#ccc' }}
                />
              </RadioGroup>
            </FormControl>
          )}

          {isPaidPublic && (
            <FormControl sx={{ display: 'block', mb: 2 }}>
              <FormLabel sx={{ color: '#999', fontSize: 13, mb: 0.5 }}>Ticket Source</FormLabel>
              <RadioGroup
                row
                value={formData.ticketSource === 'external' ? 'external' : 'internal'}
                onChange={(e) => {
                  const ticketSource = e.target.value;
                  setFormData((prev) => ({
                    ...prev,
                    ticketSource,
                    externalTicketUrl: ticketSource === 'external' ? prev.externalTicketUrl : '',
                    applyPlatformFee: ticketSource === 'internal' ? prev.applyPlatformFee : false,
                  }));
                }}
              >
                <FormControlLabel
                  value="internal"
                  control={<Radio sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />}
                  label="Our Website"
                  sx={{ color: '#ccc' }}
                />
                <FormControlLabel
                  value="external"
                  control={<Radio sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />}
                  label="External Website"
                  sx={{ color: '#ccc' }}
                />
              </RadioGroup>
            </FormControl>
          )}

          {needsExternalUrl && (
            <TextField
              fullWidth
              label="External Ticket URL"
              value={formData.externalTicketUrl}
              onChange={(e) => setFormData({ ...formData, externalTicketUrl: e.target.value })}
              placeholder="https://..."
              helperText="Get Tickets opens this URL in a new tab. No checkout on our website."
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: '#999' } }}
              InputProps={{ sx: { color: '#fff' } }}
            />
          )}

          {!isPrivateEvent && formData.entryType === 'free' && (
            <Alert severity="info" sx={{ mb: 2, backgroundColor: 'rgba(129,199,132,0.08)', color: '#81c784' }}>
              This event will show FREE ENTRY. No tickets, checkout, or guest list.
            </Alert>
          )}

          {needsInternalTickets && (
            <>
          <Divider sx={{ borderColor: '#333', my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
            <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700 }}>
              Ticket Types
            </Typography>
            <Button size="small" startIcon={<Add />} onClick={addTicketType} sx={{ color: '#667eea' }}>
              Add Type
            </Button>
          </Box>

          {formData.ticketTypes.map((type, index) => (
            <Paper
              key={type.id || `new-${index}`}
              sx={{ p: 2, mb: 2, backgroundColor: '#121210', border: '1px solid #333', borderRadius: 2 }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                <Typography variant="caption" sx={{ color: '#667eea', fontWeight: 700 }}>
                  Type {index + 1}
                </Typography>
                {formData.ticketTypes.length > 1 && (
                  <IconButton size="small" onClick={() => removeTicketType(index)} sx={{ color: '#f44336' }}>
                    <Remove fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Grid container spacing={2} sx={{ mb: 1.5 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Type Name"
                    placeholder="e.g. General, Child, Adult"
                    value={type.name}
                    onChange={(e) => updateTicketType(index, 'name', e.target.value)}
                    InputLabelProps={{ sx: { color: '#999' } }}
                    InputProps={{ sx: { color: '#fff' } }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="Price (£)"
                    type="number"
                    value={type.price}
                    onChange={(e) => updateTicketType(index, 'price', e.target.value)}
                    inputProps={{ min: 1, step: 0.01 }}
                    InputLabelProps={{ sx: { color: '#999' } }}
                    InputProps={{ sx: { color: '#fff' } }}
                  />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <TextField
                    fullWidth
                    label="Max Tickets"
                    type="number"
                    value={type.maxTickets}
                    onChange={(e) => updateTicketType(index, 'maxTickets', e.target.value)}
                    helperText="Unlimited if empty"
                    InputLabelProps={{ sx: { color: '#999' } }}
                    InputProps={{ sx: { color: '#fff' } }}
                  />
                </Grid>
              </Grid>

              <FormControlLabel
                control={
                  <Switch
                    checked={type.markSoldOut}
                    onChange={(e) => updateTicketType(index, 'markSoldOut', e.target.checked)}
                  />
                }
                label="Mark this type as Sold Out"
                sx={{ color: '#ccc', display: 'block', mb: 0.5 }}
              />

              {type.maxTickets && (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={type.showFewLeft}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFormData((prev) => {
                            const ticketTypes = [...prev.ticketTypes];
                            ticketTypes[index] = {
                              ...ticketTypes[index],
                              showFewLeft: checked,
                              showFewLeftDummy: checked ? false : ticketTypes[index].showFewLeftDummy,
                            };
                            return { ...prev, ticketTypes };
                          });
                        }}
                      />
                    }
                    label='Show "Few Left" for this type'
                    sx={{ color: '#ccc', display: 'block', mb: 0.5 }}
                  />
                  {type.showFewLeft && (
                    <TextField
                      fullWidth
                      size="small"
                      label="Few Left Threshold"
                      type="number"
                      value={type.fewLeftThreshold}
                      onChange={(e) => updateTicketType(index, 'fewLeftThreshold', e.target.value)}
                      helperText="Show badge when this many or fewer remain for this type"
                      inputProps={{ min: 1 }}
                      sx={{ mb: 1 }}
                      InputLabelProps={{ sx: { color: '#999' } }}
                      InputProps={{ sx: { color: '#fff' } }}
                    />
                  )}
                </>
              )}
              <FormControlLabel
                control={
                  <Switch
                    checked={!!type.showFewLeftDummy}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFormData((prev) => {
                        const ticketTypes = [...prev.ticketTypes];
                        ticketTypes[index] = {
                          ...ticketTypes[index],
                          showFewLeftDummy: checked,
                          showFewLeft: checked ? false : ticketTypes[index].showFewLeft,
                        };
                        return { ...prev, ticketTypes };
                      });
                    }}
                  />
                }
                label='Show "Few Left" dummy'
                sx={{ color: '#ccc', display: 'block', mb: 0.5 }}
              />
              <Typography variant="caption" sx={{ color: '#777', display: 'block', mb: 1 }}>
                Always shows a Few Left tag. Not based on remaining tickets.
              </Typography>
            </Paper>
          ))}

          <Divider sx={{ borderColor: '#333', my: 2 }} />
          <Typography variant="subtitle2" sx={{ color: '#667eea', mb: 1.5, fontWeight: 700 }}>
            Platform Fee
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={formData.applyPlatformFee}
                onChange={(e) => setFormData({
                  ...formData,
                  applyPlatformFee: e.target.checked,
                  platformFee: e.target.checked ? formData.platformFee : '',
                  platformFeeType: e.target.checked ? formData.platformFeeType : 'flat',
                })}
              />
            }
            label="Apply platform fee"
            sx={{ color: '#ccc', display: 'block', mb: 1 }}
          />
          {formData.applyPlatformFee && (
            <>
              <FormControl sx={{ display: 'block', mb: 2 }}>
                <FormLabel sx={{ color: '#999', fontSize: 13, mb: 0.5 }}>Fee type</FormLabel>
                <RadioGroup
                  row
                  value={formData.platformFeeType}
                  onChange={(e) => setFormData({ ...formData, platformFeeType: e.target.value })}
                >
                  <FormControlLabel
                    value="flat"
                    control={<Radio sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />}
                    label="Flat fee"
                    sx={{ color: '#ccc' }}
                  />
                  <FormControlLabel
                    value="per_ticket"
                    control={<Radio sx={{ color: '#667eea', '&.Mui-checked': { color: '#667eea' } }} />}
                    label="Per ticket"
                    sx={{ color: '#ccc' }}
                  />
                </RadioGroup>
              </FormControl>
              <TextField
                fullWidth
                label="Platform fee (£)"
                type="number"
                value={formData.platformFee}
                onChange={(e) => setFormData({ ...formData, platformFee: e.target.value })}
                helperText={formData.platformFeeType === 'per_ticket'
                  ? 'Added for each ticket at checkout'
                  : 'Added once per order at checkout'}
                inputProps={{ min: 0.01, step: 0.01 }}
                sx={{ mb: 2 }}
                InputLabelProps={{ sx: { color: '#999' } }}
                InputProps={{ sx: { color: '#fff' } }}
              />
            </>
          )}

          </>
          )}

          <Divider sx={{ borderColor: '#333', my: 2 }} />
          <Typography variant="subtitle2" sx={{ color: '#667eea', mb: 1.5, fontWeight: 700 }}>
            Location
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="City"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                required={isPrivateEvent}
                helperText={isPrivateEvent ? 'Shown publicly' : 'Optional'}
                InputLabelProps={{ sx: { color: '#999' } }}
                InputProps={{ sx: { color: '#fff' } }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Country"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                required={isPrivateEvent}
                helperText={isPrivateEvent ? 'Shown publicly' : 'Optional'}
                InputLabelProps={{ sx: { color: '#999' } }}
                InputProps={{ sx: { color: '#fff' } }}
              />
            </Grid>
          </Grid>
          <TextField
            fullWidth
            label="Venue"
            value={formData.venue}
            onChange={(e) => setFormData({ ...formData, venue: e.target.value })}
            helperText={isPrivateEvent ? 'Not shown publicly — only city and country appear on the listing' : ''}
            sx={{ mb: 2 }}
            InputLabelProps={{ sx: { color: '#999' } }}
            InputProps={{ sx: { color: '#fff' } }}
          />
          {!isPrivateEvent && (
            <TextField
              fullWidth
              label="Venue Location Link"
              value={formData.venueLocationLink}
              onChange={(e) => setFormData({ ...formData, venueLocationLink: e.target.value })}
              placeholder="https://maps.google.com/..."
              helperText="Google Maps or location URL shown on tickets and event page"
              sx={{ mb: 2 }}
              InputLabelProps={{ sx: { color: '#999' } }}
              InputProps={{ sx: { color: '#fff' } }}
            />
          )}
          <TextField fullWidth label="Event Date & Time" type="datetime-local" value={formData.eventDate} onChange={(e) => setFormData({ ...formData, eventDate: e.target.value })} InputLabelProps={{ shrink: true, sx: { color: '#999' } }} InputProps={{ sx: { color: '#fff' } }} sx={{ mb: 2 }} />

          {needsInternalTickets && (
            <>
          <Divider sx={{ borderColor: '#333', my: 2 }} />
          <Typography variant="subtitle2" sx={{ color: '#667eea', mb: 1.5, fontWeight: 700 }}>
            Overall Event Availability
          </Typography>

          <TextField
            fullWidth
            label="Overall Max Tickets (all types combined)"
            type="number"
            value={formData.maxTickets}
            onChange={(e) => setFormData({ ...formData, maxTickets: e.target.value })}
            helperText="Optional cap across all ticket types. Leave empty for unlimited."
            sx={{ mb: 2 }}
            InputLabelProps={{ sx: { color: '#999' } }}
            InputProps={{ sx: { color: '#fff' } }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={formData.markSoldOut}
                onChange={(e) => setFormData({ ...formData, markSoldOut: e.target.checked })}
              />
            }
            label="Mark as Sold Out (blocks purchases — use for unlimited events or manual override)"
            sx={{ color: '#ccc', display: 'block', mb: 1 }}
          />

          {formData.maxTickets && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showFewLeft}
                    onChange={(e) => setFormData({
                      ...formData,
                      showFewLeft: e.target.checked,
                      showFewLeftDummy: e.target.checked ? false : formData.showFewLeftDummy,
                    })}
                  />
                }
                label='Show "Few Tickets Left" badge when remaining tickets are low'
                sx={{ color: '#ccc', display: 'block', mb: 1 }}
              />
              {formData.showFewLeft && (
                <TextField
                  fullWidth
                  label="Few Left Threshold"
                  type="number"
                  value={formData.fewLeftThreshold}
                  onChange={(e) => setFormData({ ...formData, fewLeftThreshold: e.target.value })}
                  helperText="Show badge when this many tickets or fewer remain"
                  inputProps={{ min: 1 }}
                  sx={{ mb: 2 }}
                  InputLabelProps={{ sx: { color: '#999' } }}
                  InputProps={{ sx: { color: '#fff' } }}
                />
              )}
            </>
          )}
          <FormControlLabel
            control={
              <Switch
                checked={!!formData.showFewLeftDummy}
                onChange={(e) => setFormData({
                  ...formData,
                  showFewLeftDummy: e.target.checked,
                  showFewLeft: e.target.checked ? false : formData.showFewLeft,
                })}
              />
            }
            label='Show "Few Left" dummy'
            sx={{ color: '#ccc', display: 'block', mb: 0.5 }}
          />
          <Typography variant="caption" sx={{ color: '#777', display: 'block', mb: 2 }}>
            Always shows a Few Left tag. Not based on remaining tickets.
          </Typography>
            </>
          )}

          <FormControlLabel
            control={<Switch checked={formData.active} onChange={(e) => setFormData({ ...formData, active: e.target.checked })} />}
            label="Active (visible to users)"
            sx={{ color: '#ccc' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: '#999' }}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
            {editingEvent ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Purchases Dialog */}
      <Dialog
        open={purchasesOpen}
        onClose={() => setPurchasesOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { backgroundColor: '#1a1a1a', color: '#fff' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
            <Box>
              Purchases — {selectedEvent?.title}
              <Typography variant="body2" sx={{ color: '#999' }}>{selectedEvent?.event_name}</Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <Paper sx={{ px: 1.75, py: 1, backgroundColor: '#121210', border: '1px solid #333', borderRadius: 1.5, minWidth: 110 }}>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', lineHeight: 1.2 }}>Total Revenue</Typography>
                <Typography sx={{ color: '#4caf50', fontWeight: 800, fontSize: '1.05rem' }}>
                  {formatPrice(purchaseStats.revenue)}
                </Typography>
              </Paper>
              <Paper sx={{ px: 1.75, py: 1, backgroundColor: '#121210', border: '1px solid #333', borderRadius: 1.5, minWidth: 110 }}>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', lineHeight: 1.2 }}>Cash Collected</Typography>
                <Typography sx={{ color: '#81c784', fontWeight: 800, fontSize: '1.05rem' }}>
                  {formatPrice(purchaseStats.cashRevenue)}
                </Typography>
              </Paper>
              <Paper sx={{ px: 1.75, py: 1, backgroundColor: '#121210', border: '1px solid #333', borderRadius: 1.5, minWidth: 110 }}>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', lineHeight: 1.2 }}>Transactions</Typography>
                <Typography sx={{ color: '#667eea', fontWeight: 800, fontSize: '1.05rem' }}>
                  {purchaseStats.transactions}
                </Typography>
              </Paper>
              <Paper sx={{ px: 1.75, py: 1, backgroundColor: '#121210', border: '1px solid #333', borderRadius: 1.5, minWidth: 110 }}>
                <Typography variant="caption" sx={{ color: '#999', display: 'block', lineHeight: 1.2 }}>Tickets Sold</Typography>
                <Typography sx={{ color: '#ff9800', fontWeight: 800, fontSize: '1.05rem' }}>
                  {toCount(purchaseStats.ticketsSold)}
                </Typography>
              </Paper>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Divider sx={{ borderColor: '#333', mb: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<Payments />}
              onClick={openCashDialog}
              sx={{ background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)', textTransform: 'none', fontWeight: 700 }}
            >
              Take Cash Payment
            </Button>
          </Box>
          <Grid container spacing={1.5} sx={{ mb: 2 }}>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: '#999' }}>Coupon</InputLabel>
                <Select
                  label="Coupon"
                  value={purchaseFilters.coupon}
                  onChange={(e) => setPurchaseFilters((prev) => ({ ...prev, coupon: e.target.value }))}
                  sx={{ color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: '#333' } }}
                >
                  <MenuItem value="all">All coupons</MenuItem>
                  <MenuItem value="none">None</MenuItem>
                  {purchaseCouponOptions.map((code) => (
                    <MenuItem key={code} value={code}>{code}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: '#999' }}>Payment</InputLabel>
                <Select
                  label="Payment"
                  value={purchaseFilters.method}
                  onChange={(e) => setPurchaseFilters((prev) => ({ ...prev, method: e.target.value }))}
                  sx={{ color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: '#333' } }}
                >
                  <MenuItem value="all">All methods</MenuItem>
                  <MenuItem value="cash">Cash</MenuItem>
                  <MenuItem value="square">Card</MenuItem>
                  <MenuItem value="complimentary">Complimentary</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Purchase date"
                value={purchaseFilters.purchaseDate}
                onChange={(e) => setPurchaseFilters((prev) => ({ ...prev, purchaseDate: e.target.value }))}
                InputLabelProps={{ shrink: true, sx: { color: '#999' } }}
                InputProps={{ sx: { color: '#fff' } }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth size="small">
                <InputLabel sx={{ color: '#999' }}>Check-In</InputLabel>
                <Select
                  label="Check-In"
                  value={purchaseFilters.checkIn}
                  onChange={(e) => setPurchaseFilters((prev) => ({ ...prev, checkIn: e.target.value }))}
                  sx={{ color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: '#333' } }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="checked_in">Checked In</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          {purchases.length === 0 ? (
            <Typography sx={{ color: '#666', textAlign: 'center', py: 4 }}>No purchases yet</Typography>
          ) : filteredPurchases.length === 0 ? (
            <Typography sx={{ color: '#666', textAlign: 'center', py: 4 }}>No purchases match these filters</Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ color: '#999' }}>Ticket ID</TableCell>
                    <TableCell sx={{ color: '#999' }}>Customer</TableCell>
                    <TableCell sx={{ color: '#999' }}>Payment</TableCell>
                    <TableCell sx={{ color: '#999' }}>qty</TableCell>
                    <TableCell sx={{ color: '#999' }}>Paid</TableCell>
                    <TableCell sx={{ color: '#999' }}>Coupon</TableCell>
                    <TableCell sx={{ color: '#999' }}>Purchased</TableCell>
                    <TableCell sx={{ color: '#999' }}>Check-In</TableCell>
                    <TableCell sx={{ color: '#999' }}>Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredPurchases.map((p) => (
                    <TableRow key={p.id} hover>
                      <TableCell sx={{ color: '#667eea', fontFamily: 'monospace', fontSize: 12 }}>{p.ticket_id}</TableCell>
                      <TableCell>
                        <Typography sx={{ color: '#fff', fontSize: 13 }}>{p.name}</Typography>
                        <Typography sx={{ color: '#999', fontSize: 11 }}>{p.email}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={PAYMENT_METHOD_CHIP[paymentMethodOf(p)]?.label || 'Card'}
                          size="small"
                          sx={{
                            backgroundColor: PAYMENT_METHOD_CHIP[paymentMethodOf(p)]?.bg || 'rgba(255,255,255,0.1)',
                            color: PAYMENT_METHOD_CHIP[paymentMethodOf(p)]?.color || '#999',
                            fontWeight: 700,
                          }}
                        />
                        {paymentMethodOf(p) === 'square' && (p.card_brand || p.card_last4) && (
                          <Typography sx={{ color: '#777', fontSize: 11, mt: 0.25 }}>{formatCard(p)}</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: '#fff', fontSize: 13 }}>{toCount(p.quantity)}</TableCell>
                      <TableCell sx={{ color: '#4caf50' }}>{formatPrice(p.amount)}</TableCell>
                      <TableCell sx={{ color: '#ff9800', fontSize: 12 }}>{p.coupon_code || '—'}</TableCell>
                      <TableCell sx={{ color: '#999', fontSize: 12 }}>{formatDate(p.created_at)}</TableCell>
                      <TableCell>
                        <Chip
                          label={p.checked_in ? 'Checked In' : 'Pending'}
                          size="small"
                          sx={{
                            backgroundColor: p.checked_in ? 'rgba(76,175,80,0.2)' : 'rgba(255,255,255,0.1)',
                            color: p.checked_in ? '#4caf50' : '#999',
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Tooltip title="View full details">
                          <IconButton size="small" onClick={() => openPurchaseDetail(p)} sx={{ color: '#667eea' }}>
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPurchaseFilters({ coupon: 'all', purchaseDate: '', checkIn: 'all', method: 'all' })}
            sx={{ color: '#999' }}
            disabled={
              purchaseFilters.coupon === 'all'
              && !purchaseFilters.purchaseDate
              && purchaseFilters.checkIn === 'all'
              && purchaseFilters.method === 'all'
            }
          >
            Clear filters
          </Button>
          <Button onClick={() => setPurchasesOpen(false)} sx={{ color: '#999' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Take Cash Payment Dialog */}
      <Dialog
        open={cashOpen}
        onClose={() => !cashSubmitting && setCashOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { backgroundColor: '#121210', color: '#fff' } }}
      >
        <DialogTitle>
          {cashResult ? 'Payment complete' : 'Take Cash Payment'}
          <Typography variant="body2" sx={{ color: '#999' }}>
            {selectedEvent?.title}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {cashError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setCashError('')}>{cashError}</Alert>}
          {cashActionMessage && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setCashActionMessage('')}>
              {cashActionMessage}
            </Alert>
          )}

          {cashResult ? (
            <Box>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CheckCircle sx={{ fontSize: 56, color: '#81c784', mb: 1 }} />
                <Typography sx={{ fontWeight: 800, fontSize: '1.25rem', mb: 0.5 }}>
                  {formatPrice(cashResult.amount)} received
                </Typography>
                <Typography sx={{ color: '#999', fontSize: 14 }}>
                  {toCount(cashResult.quantity)} ticket{toCount(cashResult.quantity) === 1 ? '' : 's'} issued
                  {cashResult.customerMode === 'box_office' ? ' · Box Office' : cashResult.name ? ` · ${cashResult.name}` : ''}
                </Typography>
                {cashResult.emailSent && (
                  <Typography sx={{ color: '#81c784', fontSize: 13, mt: 0.5 }}>
                    Emailed to {cashResult.email}
                  </Typography>
                )}
                <Typography sx={{ color: '#667eea', fontFamily: 'monospace', fontSize: 12, mt: 1 }}>
                  {cashResult.ticketId}
                </Typography>
              </Box>

              <Paper sx={{ p: 2, mb: 2, backgroundColor: '#1a1a1a', border: '1px solid #333', borderRadius: 2 }}>
                {(cashResult.entries || []).map((entry, index) => (
                  <Box
                    key={entry.entryTicketId}
                    sx={{
                      py: 1,
                      borderBottom: index < cashResult.entries.length - 1 ? '1px solid #2a2a2a' : 'none',
                    }}
                  >
                    <Typography sx={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>
                      {index + 1}. {entry.typeName}
                    </Typography>
                    <Typography sx={{ color: '#999', fontSize: 12, fontFamily: 'monospace' }}>
                      {entry.entryTicketId} · {entry.verificationCode}
                    </Typography>
                  </Box>
                ))}
              </Paper>

              <Button
                fullWidth
                variant="contained"
                size="large"
                startIcon={cashDownloading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <Download />}
                onClick={() => handleDownloadPdf(cashResult.ticketId, 'cash')}
                disabled={cashDownloading}
                sx={{
                  mb: 2,
                  py: 1.25,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  textTransform: 'none',
                  fontWeight: 700,
                }}
              >
                {cashDownloading ? 'Preparing PDF...' : 'Download tickets (PDF)'}
              </Button>

              <Typography variant="subtitle2" sx={{ color: '#999', mb: 1.25, fontWeight: 700 }}>
                Send the ticket PDF to the customer
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={shareBusy ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <WhatsApp />}
                    onClick={() => sharePdf(cashResult.ticketId, cashResult, { context: 'cash', whatsapp: true })}
                    disabled={shareBusy}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      backgroundColor: '#25D366',
                      '&:hover': { backgroundColor: '#1ebe57' },
                    }}
                  >
                    WhatsApp PDF
                  </Button>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Send />}
                    onClick={() => openEmailPrompt(cashResult.ticketId, cashResult.email, 'cash')}
                    sx={{ textTransform: 'none', borderColor: '#ffb74d', color: '#ffb74d' }}
                  >
                    Email PDF
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<Share />}
                    onClick={() => sharePdf(cashResult.ticketId, cashResult, { context: 'cash' })}
                    disabled={shareBusy}
                    sx={{ textTransform: 'none', borderColor: '#555', color: '#fff' }}
                  >
                    Share more
                  </Button>
                </Grid>
                <Grid item xs={6}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<ContentCopy />}
                    onClick={handleCopyCashDetails}
                    sx={{ textTransform: 'none', borderColor: '#555', color: '#ccc' }}
                  >
                    Copy details
                  </Button>
                </Grid>
              </Grid>
            </Box>
          ) : (
            <Box>
              <Typography variant="subtitle2" sx={{ color: '#999', mb: 1, fontWeight: 700 }}>
                Who is this for?
              </Typography>
              <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={cashForm.customerMode}
                onChange={setCashCustomerMode}
                sx={{
                  mb: 2,
                  '& .MuiToggleButton-root': {
                    color: '#999',
                    borderColor: '#333',
                    textTransform: 'none',
                    py: 1,
                  },
                  '& .Mui-selected': {
                    color: '#fff !important',
                    backgroundColor: 'rgba(102,126,234,0.25) !important',
                    borderColor: '#667eea !important',
                  },
                }}
              >
                <ToggleButton value="customer">
                  <Person sx={{ mr: 0.75, fontSize: 18 }} />
                  Customer details
                </ToggleButton>
                <ToggleButton value="box_office">
                  <Storefront sx={{ mr: 0.75, fontSize: 18 }} />
                  Box Office
                </ToggleButton>
              </ToggleButtonGroup>

              {cashForm.customerMode === 'box_office' ? (
                <Alert
                  severity="info"
                  icon={<Storefront />}
                  sx={{
                    mb: 2,
                    backgroundColor: 'rgba(102,126,234,0.12)',
                    color: '#c5cae9',
                    border: '1px solid rgba(102,126,234,0.35)',
                    '& .MuiAlert-icon': { color: '#8c9eff' },
                  }}
                >
                  Issued under <strong>Box Office</strong> — no name or email needed.
                  Download or share the tickets after payment.
                </Alert>
              ) : (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Name (optional)"
                        value={cashForm.name}
                        onChange={(e) => setCashForm((prev) => ({ ...prev, name: e.target.value }))}
                        InputLabelProps={{ sx: { color: '#999' } }}
                        InputProps={{ sx: { color: '#fff' } }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        size="small"
                        type="email"
                        label={cashForm.sendEmail ? 'Email (required to send)' : 'Email (optional)'}
                        value={cashForm.email}
                        onChange={(e) => setCashForm((prev) => ({ ...prev, email: e.target.value }))}
                        InputLabelProps={{ sx: { color: '#999' } }}
                        InputProps={{ sx: { color: '#fff' } }}
                      />
                    </Grid>
                  </Grid>
                  <FormControlLabel
                    sx={{ mt: 1, mb: 1, color: '#ccc' }}
                    control={
                      <Switch
                        checked={cashForm.sendEmail}
                        onChange={(e) => setCashForm((prev) => ({ ...prev, sendEmail: e.target.checked }))}
                      />
                    }
                    label="Email tickets and PDF to the customer"
                  />
                </>
              )}

              <Typography variant="subtitle2" sx={{ color: '#667eea', fontWeight: 700, mt: 1, mb: 1 }}>
                Tickets
              </Typography>

              {cashTicketTypes.length === 0 ? (
                <Typography sx={{ color: '#666', fontSize: 13 }}>
                  This event has no ticket types to sell.
                </Typography>
              ) : cashTicketTypes.map((type) => {
                const qty = toCount(cashForm.quantities[type.id]);
                return (
                  <Box
                    key={type.id}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 2,
                      py: 1,
                      borderBottom: '1px solid #2a2a2a',
                    }}
                  >
                    <Box>
                      <Typography sx={{ color: '#fff', fontSize: 14 }}>{type.name}</Typography>
                      <Typography sx={{ color: '#999', fontSize: 12 }}>
                        {formatPrice(type.price_pence)}
                        {type.tickets_remaining != null ? ` · ${type.tickets_remaining} left` : ''}
                        {type.mark_sold_out ? ' · marked sold out online' : ''}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <IconButton
                        size="small"
                        onClick={() => setCashQuantity(type.id, qty - 1)}
                        disabled={qty === 0}
                        sx={{ color: '#999' }}
                      >
                        <Remove fontSize="small" />
                      </IconButton>
                      <Typography sx={{ color: '#fff', minWidth: 24, textAlign: 'center' }}>{qty}</Typography>
                      <IconButton
                        size="small"
                        onClick={() => setCashQuantity(type.id, qty + 1)}
                        sx={{ color: '#4caf50' }}
                      >
                        <Add fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                );
              })}

              <Typography variant="subtitle2" sx={{ color: '#999', fontWeight: 700, mt: 2.5, mb: 1 }}>
                Discount code
              </Typography>
              {appliedCoupon ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    p: 1.25,
                    borderRadius: 2,
                    border: '1px solid rgba(76,175,80,0.45)',
                    backgroundColor: 'rgba(76,175,80,0.12)',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <LocalOffer sx={{ color: '#81c784', fontSize: 18 }} />
                    <Box>
                      <Typography sx={{ color: '#81c784', fontWeight: 700, fontSize: 14 }}>
                        {appliedCoupon} applied
                      </Typography>
                      {!!cashQuote?.discount && (
                        <Typography sx={{ color: '#9ccc9f', fontSize: 12 }}>
                          Saves {formatPrice(cashQuote.discount)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  <Button size="small" onClick={handleRemoveCoupon} sx={{ color: '#ef9a9a', textTransform: 'none' }}>
                    Remove
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Enter code"
                    value={cashForm.couponCode}
                    onChange={(e) => setCashForm((prev) => ({ ...prev, couponCode: e.target.value.toUpperCase() }))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleApplyCoupon();
                      }
                    }}
                    InputProps={{ sx: { color: '#fff' } }}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleApplyCoupon}
                    disabled={!cashForm.couponCode.trim() || applyingCoupon || cashItems.length === 0}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      minWidth: 96,
                      borderColor: '#667eea',
                      color: '#8c9eff',
                    }}
                  >
                    {applyingCoupon ? <CircularProgress size={18} /> : 'Apply'}
                  </Button>
                </Box>
              )}

              <TextField
                fullWidth
                size="small"
                label="Notes (optional)"
                placeholder="e.g. paid at door"
                value={cashForm.notes}
                onChange={(e) => setCashForm((prev) => ({ ...prev, notes: e.target.value }))}
                InputLabelProps={{ sx: { color: '#999' } }}
                InputProps={{ sx: { color: '#fff' } }}
                sx={{ mt: 2 }}
              />

              {cashQuoteError && <Alert severity="warning" sx={{ mt: 2 }}>{cashQuoteError}</Alert>}

              <Paper
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #333',
                  borderRadius: 2,
                }}
              >
                <DetailRow label="Tickets" value={cashQuote ? toCount(cashQuote.totalQuantity) : '—'} />
                <DetailRow label="Subtotal" value={cashQuote ? formatPrice(cashQuote.subtotal) : '—'} />
                {!!cashQuote?.discount && (
                  <DetailRow label="Discount" value={`-${formatPrice(cashQuote.discount)}`} />
                )}
                {!!cashQuote?.platformFee && (
                  <DetailRow
                    label="Platform fee"
                    value={cashQuote.chargedPlatformFee === 0
                      ? `${formatPrice(cashQuote.platformFee)} waived`
                      : formatPrice(cashQuote.chargedPlatformFee)}
                  />
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 1.5 }}>
                  <Typography sx={{ color: '#fff', fontWeight: 700 }}>Cash to collect</Typography>
                  <Typography sx={{ color: '#81c784', fontWeight: 800, fontSize: '1.4rem' }}>
                    {cashQuote ? formatPrice(cashQuote.total) : '—'}
                  </Typography>
                </Box>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          {cashResult ? (
            <>
              <Button onClick={() => setCashOpen(false)} sx={{ color: '#999' }}>
                Done
              </Button>
              <Button
                variant="outlined"
                onClick={resetCashDialogForNextSale}
                sx={{ textTransform: 'none', borderColor: '#555', color: '#fff' }}
              >
                Next cash sale
              </Button>
            </>
          ) : (
            <>
              <Button onClick={() => setCashOpen(false)} disabled={cashSubmitting} sx={{ color: '#999' }}>
                Close
              </Button>
              <Button
                variant="contained"
                startIcon={cashSubmitting ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Payments />}
                onClick={handleIssueCashTicket}
                disabled={cashSubmitting || !cashQuote}
                sx={{ background: 'linear-gradient(135deg, #2e7d32 0%, #43a047 100%)', textTransform: 'none', fontWeight: 700 }}
              >
                {cashSubmitting
                  ? 'Issuing...'
                  : `Confirm cash received${cashQuote ? ` · ${formatPrice(cashQuote.total)}` : ''}`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Purchase Detail Dialog */}
      <Dialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { backgroundColor: '#121210', color: '#fff' } }}
      >
        {selectedPurchase && (
          <>
            <DialogTitle>
              Ticket Details
              <Typography variant="body2" sx={{ color: '#667eea', fontFamily: 'monospace' }}>
                {selectedPurchase.ticket_id}
              </Typography>
            </DialogTitle>
            <DialogContent>
              {detailError && <Alert severity="error" sx={{ mb: 2 }}>{detailError}</Alert>}
              {detailMessage && <Alert severity="success" sx={{ mb: 2 }}>{detailMessage}</Alert>}
              <Grid container spacing={2}>
                <Grid item xs={12} md={7}>
                  <SectionCard title="Customer" icon={<Person sx={{ color: '#667eea', fontSize: 18 }} />}>
                    <DetailRow label="Name" value={selectedPurchase.name} />
                    <DetailRow label="Email" value={selectedPurchase.email} />
                    <DetailRow label="Quantity" value={toCount(selectedPurchase.quantity)} />
                  </SectionCard>

                  <SectionCard title="Payment" icon={<CreditCard sx={{ color: '#667eea', fontSize: 18 }} />}>
                    <DetailRow
                      label="Method"
                      value={PAYMENT_METHOD_CHIP[paymentMethodOf(selectedPurchase)]?.label || 'Card'}
                    />
                    {paymentMethodOf(selectedPurchase) === 'square' && (
                      <DetailRow label="Card" value={formatCard(selectedPurchase)} />
                    )}
                    {selectedPurchase.issued_by && (
                      <DetailRow label="Issued By" value={selectedPurchase.issued_by} />
                    )}
                    {selectedPurchase.admin_notes && (
                      <DetailRow label="Notes" value={selectedPurchase.admin_notes} />
                    )}
                    <DetailRow label="Payment ID" value={selectedPurchase.payment_id} mono />
                    <DetailRow label="Payment Status" value={selectedPurchase.payment_status} />
                    <DetailRow label="Source Type" value={selectedPurchase.source_type} />
                    <DetailRow label="Subtotal" value={formatPrice(selectedPurchase.original_amount)} />
                    <DetailRow label="Discount" value={selectedPurchase.discount_amount ? `-${formatPrice(selectedPurchase.discount_amount)}` : '—'} />
                    <DetailRow
                      label="Platform Fee"
                      value={
                        selectedPurchase.platform_fee_pence
                          ? (() => {
                              const original = selectedPurchase.platform_fee_pence;
                              const charged = selectedPurchase.platform_fee_charged_pence != null
                                ? selectedPurchase.platform_fee_charged_pence
                                : (selectedPurchase.platform_fee_waived ? 0 : original);
                              if (charged === 0) return `${formatPrice(original)} waived`;
                              if (charged < original) return `${formatPrice(charged)} (was ${formatPrice(original)})`;
                              return formatPrice(original);
                            })()
                          : '—'
                      }
                    />
                    <DetailRow label="Amount Paid" value={formatPrice(selectedPurchase.amount)} />
                  </SectionCard>

                  <SectionCard title="Coupon" icon={<LocalOffer sx={{ color: '#ff9800', fontSize: 18 }} />}>
                    <DetailRow label="Discount Code" value={selectedPurchase.coupon_code} mono />
                    <DetailRow label="Discount Applied" value={selectedPurchase.discount_amount ? formatPrice(selectedPurchase.discount_amount) : '—'} />
                  </SectionCard>

                  <SectionCard title="Timestamps" icon={<Receipt sx={{ color: '#667eea', fontSize: 18 }} />}>
                    <DetailRow label="Purchased At" value={formatDate(selectedPurchase.created_at)} />
                    <DetailRow label="Check-In Status" value={selectedPurchase.checked_in ? 'Checked In' : 'Not Checked In'} />
                    <DetailRow label="Checked In At" value={selectedPurchase.checked_in ? formatDate(selectedPurchase.checked_in_at) : '—'} />
                  </SectionCard>
                </Grid>

                <Grid item xs={12} md={5}>
                  <SectionCard title="Tickets & QR Codes" icon={<QrCodeScanner sx={{ color: '#667eea', fontSize: 18 }} />}>
                    <DetailRow label="Order ID" value={selectedPurchase.ticket_id} mono />
                    <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 1, mb: 1.5 }}>
                      {toCount(selectedPurchase.quantity)} ticket{toCount(selectedPurchase.quantity) === 1 ? '' : 's'} in this purchase
                    </Typography>
                    {(selectedPurchase.entries?.length
                      ? selectedPurchase.entries
                      : [{
                          entry_ticket_id: selectedPurchase.ticket_id,
                          verification_code: selectedPurchase.verification_code,
                          type_name: 'General Admission',
                          qr_image_url: selectedPurchase.qr_image_url,
                        }]
                    ).map((entry, index, list) => (
                      <Box
                        key={entry.entry_ticket_id || `${selectedPurchase.ticket_id}-${index}`}
                        sx={{ mb: 2, p: 1.5, border: '1px solid #333', borderRadius: 2, backgroundColor: '#151515' }}
                      >
                        <Typography variant="caption" sx={{ color: '#667eea', fontWeight: 700, displayTransform: 'uppercase', letterSpacing: 0.6 }}>
                          Ticket {index + 1} of {list.length}
                          {entry.type_name ? ` · ${entry.type_name}` : ''}
                        </Typography>
                        <DetailRow label="Ticket ID" value={entry.entry_ticket_id} mono />
                        <DetailRow label="Verification Code" value={entry.verification_code} mono />
                        {entry.checked_in != null && (
                          <DetailRow label="Check-In" value={entry.checked_in ? 'Checked In' : 'Pending'} />
                        )}
                        <Box sx={{ textAlign: 'center', mt: 1.5, p: 1.5, bgcolor: '#fff', borderRadius: 2 }}>
                          <Box
                            component="img"
                            src={qrImageSrc(entry.entry_ticket_id, entry.qr_image_url)}
                            alt={`QR ${entry.entry_ticket_id}`}
                            sx={{ width: 160, height: 160 }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </SectionCard>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={shareBusy ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <WhatsApp />}
                onClick={() => sharePdf(
                  selectedPurchase.ticket_id,
                  { ...selectedPurchase, event_title: selectedEvent?.title, event_date: selectedEvent?.event_date, venue: selectedEvent?.venue },
                  { context: 'detail', whatsapp: true }
                )}
                disabled={shareBusy}
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  backgroundColor: '#25D366',
                  '&:hover': { backgroundColor: '#1ebe57' },
                }}
              >
                WhatsApp PDF
              </Button>
              <Button
                variant="outlined"
                startIcon={<Share />}
                onClick={() => sharePdf(
                  selectedPurchase.ticket_id,
                  { ...selectedPurchase, event_title: selectedEvent?.title, event_date: selectedEvent?.event_date, venue: selectedEvent?.venue },
                  { context: 'detail' }
                )}
                disabled={shareBusy}
                sx={{ textTransform: 'none', borderColor: '#555', color: '#fff' }}
              >
                Share more (PDF)
              </Button>
              <Button
                variant="outlined"
                startIcon={downloadingTicketId ? <CircularProgress size={16} /> : <Download />}
                onClick={() => handleDownloadPdf(selectedPurchase.ticket_id, 'detail')}
                disabled={!!downloadingTicketId}
                sx={{ textTransform: 'none', borderColor: '#555', color: '#fff' }}
              >
                {downloadingTicketId ? 'Downloading...' : 'Download PDF'}
              </Button>
              <Button
                variant="outlined"
                startIcon={<Send />}
                onClick={() => openEmailPrompt(selectedPurchase.ticket_id, selectedPurchase.email, 'detail')}
                sx={{ textTransform: 'none', borderColor: '#ffb74d', color: '#ffb74d' }}
              >
                Email PDF
              </Button>
              <Button
                variant="contained"
                startIcon={<Send />}
                onClick={handleResendTicketEmail}
                disabled={!!resendingTicketId || isPlaceholderEmail(selectedPurchase.email)}
                sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', textTransform: 'none' }}
              >
                {resendingTicketId ? 'Sending...' : 'Resend to buyer'}
              </Button>
              <Button onClick={() => setDetailOpen(false)} sx={{ color: '#999' }}>Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Email the ticket PDF to any address */}
      <Dialog
        open={!!emailPrompt}
        onClose={() => !emailPromptSending && setEmailPrompt(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { backgroundColor: '#121210', color: '#fff' } }}
      >
        <DialogTitle>
          Email ticket PDF
          <Typography variant="body2" sx={{ color: '#999' }}>
            The customer receives the same email and PDF as an online order.
          </Typography>
        </DialogTitle>
        <DialogContent>
          {emailPromptError && <Alert severity="error" sx={{ mb: 2 }}>{emailPromptError}</Alert>}
          <TextField
            autoFocus
            fullWidth
            size="small"
            type="email"
            label="Customer email"
            value={emailPromptValue}
            onChange={(e) => setEmailPromptValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSendPdfToEmail();
              }
            }}
            InputLabelProps={{ sx: { color: '#999' } }}
            InputProps={{ sx: { color: '#fff' } }}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEmailPrompt(null)} disabled={emailPromptSending} sx={{ color: '#999' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            startIcon={emailPromptSending ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : <Send />}
            onClick={handleSendPdfToEmail}
            disabled={emailPromptSending || !emailPromptValue.trim()}
            sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', textTransform: 'none', fontWeight: 700 }}
          >
            {emailPromptSending ? 'Sending...' : 'Send PDF'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminTicketDashboard;
